import * as styles from "./AbsenceOverview.css";

import { useAtomValue } from "jotai";
import { useMemo } from "react";

import { BarChart } from "@frontend/charts/BarChart";
import { ADJACENT_CHART_PALETTE } from "@frontend/charts/shared/palette";
import { useIntlLocale } from "@frontend/hooks/useIntlLocale";
import { Trans, useLingui } from "@lingui/react/macro";

import { absencesAtom, balanceAtom } from "../stores";

const MONTHS = 12;

const MAX_REASONS = ADJACENT_CHART_PALETTE.length;

const BAR_HEIGHT = 200;

interface Entry {
  month: number;
  days: number;
  reason: string;
}

export function AbsenceOverview() {
  const { t } = useLingui();
  const locale = useIntlLocale();

  const absences = useAtomValue(absencesAtom);
  const balance = useAtomValue(balanceAtom);

  const year = balance?.year ?? new Date().getFullYear();

  const number = useMemo(
    () => new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }),
    [locale],
  );

  const entries = useMemo<Entry[]>(() => {
    const rows: Entry[] = [];

    for (const absence of absences) {
      if (null === absence.beginDate) {
        continue;
      }

      const date = new Date(absence.beginDate);

      if (date.getFullYear() !== year) {
        continue;
      }

      rows.push({
        month: date.getMonth(),
        days: absence.dayCount,
        reason: absence.typeLabel ?? absence.typeDescription ?? t`Other`,
      });
    }

    return rows;
  }, [absences, t, year]);

  const monthly = useMemo(() => {
    const month = new Intl.DateTimeFormat(locale, { month: "short" });

    const rows = Array.from({ length: MONTHS }, (_, index) => ({
      month: month.format(new Date(year, index, 1)),
      days: 0,
    }));

    for (const entry of entries) {
      rows[entry.month].days += entry.days;
    }

    return rows;
  }, [entries, locale, year]);

  const reasons = useMemo(() => {
    const totals = new Map<string, number>();

    for (const entry of entries) {
      totals.set(entry.reason, (totals.get(entry.reason) ?? 0) + entry.days);
    }

    const sorted = [...totals]
      .map(([reason, days]) => ({ reason, days }))
      .sort((a, b) => b.days - a.days);

    if (sorted.length <= MAX_REASONS) {
      return sorted;
    }

    return [
      ...sorted.slice(0, MAX_REASONS - 1),
      {
        reason: t`Other`,
        days: sorted
          .slice(MAX_REASONS - 1)
          .reduce((sum, row) => sum + row.days, 0),
      },
    ];
  }, [entries, t]);

  const days = entries.reduce((sum, entry) => sum + entry.days, 0);
  const total = number.format(days);

  return (
    <div className={styles.overview}>
      <div className={styles.head}>
        <h2 className={styles.title}>
          <Trans>Absences {year}</Trans>
        </h2>

        <span className={styles.total}>
          <Trans>{total} days in total</Trans>
        </span>
      </div>

      {0 === entries.length ? (
        <p className={styles.empty}>
          <Trans>No absence recorded for this year.</Trans>
        </p>
      ) : (
        <>
          <section className={styles.card}>
            <h3 className={styles.cardTitle}>
              <Trans>Days per month</Trans>
            </h3>

            <BarChart
              data={monthly}
              x="month"
              series={[{ key: "days", label: t`Days` }]}
              ariaLabel={t`Absence days per month`}
              valueFormat={{ maximumFractionDigits: 1 }}
              locale={locale}
              height={BAR_HEIGHT}
            />
          </section>

          <section className={styles.card}>
            <h3 className={styles.cardTitle}>
              <Trans>By reason</Trans>
            </h3>

            <ul className={styles.reasons}>
              {reasons.map((row, index) => {
                const color = ADJACENT_CHART_PALETTE[index];
                const value = number.format(row.days);

                return (
                  <li key={row.reason} className={styles.reason}>
                    <span className={styles.reasonHead}>
                      <span className={styles.reasonLabel}>{row.reason}</span>

                      <span className={styles.reasonValue}>
                        <Trans>{value} d</Trans>
                      </span>
                    </span>

                    <span className={styles.track}>
                      <span
                        className={styles.fill}
                        style={{
                          width: `${(row.days / days) * 100}%`,
                          backgroundColor: color,
                        }}
                      />
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        </>
      )}
    </div>
  );
}
